import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api, { refreshCsrfToken } from './api'
import logger from '@/utils/logger'

interface User {
  id: number
  username: string
  role: string
  role_id: number
  client_role_id?: number | null
  client_id: number | null
  client_name?: string
  first_name: string
  last_name: string
  auth_method?: string
  isStaff: boolean
  permissions: string[]
  isImpersonating?: boolean
  portal_features?: Record<string, boolean> | null
}

interface LoginCredentials {
  username: string
  password: string
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const isLoading = ref(true)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  
  const permissions = computed(() => user.value?.permissions || [])

  // F-018 / CLAUDE.md Lesson 17: These role-name string comparisons are known tech debt.
  // The correct pattern is permission-based checks (hasPermission). However, replacing these
  // computed getters would require touching 19+ views that depend on isStaff, isAdmin, etc.
  // Flagged for a dedicated refactor sprint — do not inline-fix during feature work.
  const isStaff = computed(() => {
    if (!user.value) return false
    // Use backend-provided isStaff flag, fallback to role check
    return user.value.isStaff || ['Super Admin', 'Admin', 'Support Engineer'].includes(user.value.role)
  })

  const isAdmin = computed(() => {
    if (!user.value) return false
    return ['Super Admin', 'Admin'].includes(user.value.role)
  })

  const isSuperAdmin = computed(() => {
    return user.value?.role === 'Super Admin'
  })

  const isClientUser = computed(() => {
    if (!user.value) return false
    return ['Client Admin', 'Client Employee', 'Client Read-Only'].includes(user.value.role)
  })

  const isClientEmployee = computed(() => {
    return user.value?.role === 'Client Employee'
  })

  const isClientAdmin = computed(() => {
    return user.value?.role === 'Client Admin'
  })

  const fullName = computed(() => {
    if (!user.value) return ''
    const name = `${user.value.first_name || ''} ${user.value.last_name || ''}`.trim()
    return name || user.value.username
  })

  const initials = computed(() => {
    if (!user.value) return ''
    const first = user.value.first_name?.[0] || ''
    const last = user.value.last_name?.[0] || ''
    return (first + last).toUpperCase() || user.value.username[0].toUpperCase()
  })

  const impersonating = computed(() => user.value?.isImpersonating || false)

  // Actions
  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      const response = await api.post('/login', credentials)
      // Backend returns { message, user } on success
      if (response.data.user) {
        user.value = response.data.user
        return true
      }
      return false
    } catch (error) {
      logger.error('Login failed', error)
      return false
    }
  }

  const logout = async () => {
    try {
      await api.post('/logout')
    } catch (error) {
      logger.error('Logout error', error)
    } finally {
      user.value = null
      // Clear session-scoped localStorage
      localStorage.removeItem('vhq_recent_searches')
      localStorage.removeItem('vhq_notifications')
      localStorage.removeItem('tickets_last_board')
      // Clear timer keys (dynamic names with vhq_timer_ prefix)
      Object.keys(localStorage).filter(k => k.startsWith('vhq_timer')).forEach(k => localStorage.removeItem(k))
    }
  }

  const fetchUser = async () => {
    isLoading.value = true
    try {
      const response = await api.get('/user')
      if (response.data.user) {
        user.value = response.data.user
      }
    } catch {
      user.value = null
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      const response = await api.post('/change-password', {
        currentPassword,
        newPassword,
      })
      return !!response.data.message
    } catch (error) {
      logger.error('Password change failed', error)
      return false
    }
  }

  // Portal feature helpers (client-facing module toggles)
  const hasPortalFeature = (feature: string): boolean => {
    // Staff always has access to everything
    if (user.value?.isStaff) return true
    // If no portal_features configured on the client, default to enabled
    if (!user.value?.portal_features) return true
    // Explicitly check the toggle — missing key defaults to enabled
    return user.value.portal_features[feature] !== false
  }

  // Permission helpers
  const hasPermission = (permission: string): boolean => {
    if (!user.value) return false
    // Super Admin bypasses all permission checks
    if (user.value.role === 'Super Admin') return true
    return user.value.permissions?.includes(permission) || false
  }

  const hasAnyPermission = (permissionList: string[]): boolean => {
    if (!user.value) return false
    if (user.value.role === 'Super Admin') return true
    return permissionList.some(p => user.value?.permissions?.includes(p))
  }

  const hasAllPermissions = (permissionList: string[]): boolean => {
    if (!user.value) return false
    if (user.value.role === 'Super Admin') return true
    return permissionList.every(p => user.value?.permissions?.includes(p))
  }

  // Impersonation
  const impersonate = async (userId: number): Promise<boolean> => {
    try {
      const response = await api.post(`/users/${userId}/impersonate`)
      if (response.data.success || response.data.user) {
        await fetchUser()
        // L-2: Backend invalidates CSRF token on impersonation — proactively refresh
        // to avoid a wasted round-trip through the 403 retry interceptor
        await refreshCsrfToken()
        return true
      }
      return false
    } catch (error) {
      logger.error('Impersonation failed', error)
      return false
    }
  }

  const stopImpersonation = async (): Promise<boolean> => {
    try {
      await api.post('/users/stop-impersonation')
      // Full page reload to cleanly restore original staff session
      // (avoids stale client-role state in Pinia stores and components)
      window.location.href = '/'
      return true
    } catch (error) {
      logger.error('Stop impersonation failed', error)
      return false
    }
  }

  return {
    // State
    user,
    isLoading,
    // Getters
    isAuthenticated,
    permissions,
    isStaff,
    isAdmin,
    isSuperAdmin,
    isClientUser,
    isClientEmployee,
    isClientAdmin,
    fullName,
    initials,
    impersonating,
    // Actions
    login,
    logout,
    fetchUser,
    changePassword,
    hasPortalFeature,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    impersonate,
    stopImpersonation,
  }
})
